import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getSmtpSettings, updateSmtpSettings, sendSmtpTestEmail, sendEmailTemplateTest } from '../../../services/SettingsService';

const DEFAULTS = { host: 'smtp.gmail.com', port: 587, username: '' };
const REVIEW_TEST_RECIPIENT = 'orders@tabliya.nl'; // the review request's "Send test" always goes here (a [TEST] copy with sample data)

export default function EmailSendingSettings() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [host, setHost] = useState(DEFAULTS.host);
    const [port, setPort] = useState(DEFAULTS.port);
    const [username, setUsername] = useState(DEFAULTS.username);
    const [appPasswordSet, setAppPasswordSet] = useState(false);
    const [replacingPassword, setReplacingPassword] = useState(false);
    const [newAppPassword, setNewAppPassword] = useState('');
    const [autoSendOnShopifySync, setAutoSendOnShopifySync] = useState(true);
    const [autoSendDryRun, setAutoSendDryRun] = useState(true);
    const [sendOrderConfirmation, setSendOrderConfirmation] = useState(false); // separate switch, OFF by default
    const [confirmationGoLiveAt, setConfirmationGoLiveAt] = useState(null);
    const [sendReviewRequest, setSendReviewRequest] = useState(false); // separate switch, OFF by default
    const [reviewGoLiveAt, setReviewGoLiveAt] = useState(null);
    const [reviewTesting, setReviewTesting] = useState(false);
    const [reviewDelayHours, setReviewDelayHours] = useState('72'); // hours between Delivered and the review request (default 72)
    const [goLiveAt, setGoLiveAt] = useState(null);
    const [testRecipients, setTestRecipients] = useState('');
    const [savedTestRecipients, setSavedTestRecipients] = useState('');   // what the server has saved: drives the status line under the field
    const [testEmailTo, setTestEmailTo] = useState('');

    const load = async () => {
        try {
            setLoading(true);
            const res = await getSmtpSettings();
            const data = res.data || {};
            setHost(data.host || DEFAULTS.host);
            setPort(data.port || DEFAULTS.port);
            setUsername(data.username || '');
            setAppPasswordSet(!!data.app_password_set);
            setAutoSendOnShopifySync(data.auto_send_on_shopify_sync !== false);
            setAutoSendDryRun(data.auto_send_dry_run !== false);
            setSendOrderConfirmation(data.send_order_confirmation === true);
            setConfirmationGoLiveAt(data.order_confirmation_go_live_at || null);
            setSendReviewRequest(data.send_review_request === true);
            setReviewGoLiveAt(data.review_request_go_live_at || null);
            setReviewDelayHours(String(data.review_request_delay_hours || 72));
            setGoLiveAt(data.auto_send_go_live_at || null);
            setTestRecipients(data.auto_send_test_recipients || '');
            setSavedTestRecipients(data.auto_send_test_recipients || '');
        } catch {
            toast.error(t('emailSendingSettings.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                host: host.trim(),
                port: parseInt(port, 10) || 587,
                username: username.trim(),
                auto_send_on_shopify_sync: autoSendOnShopifySync,
                auto_send_dry_run: autoSendDryRun,
                send_order_confirmation: sendOrderConfirmation,
                send_review_request: sendReviewRequest,
                review_request_delay_hours: parseInt(reviewDelayHours, 10) || 72,
                auto_send_test_recipients: testRecipients.trim(),
            };
            if (replacingPassword && newAppPassword.trim()) {
                payload.app_password = newAppPassword.trim();
            }
            const res = await updateSmtpSettings(payload);
            setAppPasswordSet(!!res.data.app_password_set);
            setGoLiveAt(res.data.auto_send_go_live_at || null);
            setConfirmationGoLiveAt(res.data.order_confirmation_go_live_at || null);
            setReviewGoLiveAt(res.data.review_request_go_live_at || null);
            setReviewDelayHours(String(res.data.review_request_delay_hours || 72));
            setTestRecipients(res.data.auto_send_test_recipients || '');
            setSavedTestRecipients(res.data.auto_send_test_recipients || '');
            setReplacingPassword(false);
            setNewAppPassword('');
            toast.success(t('emailSendingSettings.success.saved'));
        } catch (err) {
            toast.error(err.response?.data?.error || t('emailSendingSettings.errors.save'));
        } finally {
            setSaving(false);
        }
    };

    const handleReviewTest = async () => {
        setReviewTesting(true);
        try {
            const res = await sendEmailTemplateTest('review_request', { to: REVIEW_TEST_RECIPIENT });
            toast.success(t('emailSendingSettings.reviewTestSent', { to: res.data.to }));
        } catch (err) {
            toast.error(err.response?.data?.error || t('emailSendingSettings.errors.testFailed'));
        } finally {
            setReviewTesting(false);
        }
    };

    const handleTestEmail = async () => {
        setTesting(true);
        try {
            const res = await sendSmtpTestEmail(testEmailTo.trim() || undefined);
            toast.success(t('emailSendingSettings.success.testSent', { to: res.data.to }));
        } catch (err) {
            toast.error(err.response?.data?.error || t('emailSendingSettings.errors.testFailed'));
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <div>
                                <h4 className="heading mb-0">{t('emailSendingSettings.title')}</h4>
                                <span className="text-muted">{t('emailSendingSettings.subtitle')}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status" />
                                </div>
                            ) : (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('emailSendingSettings.hostLabel')}</Form.Label>
                                                <Form.Control
                                                    value={host}
                                                    onChange={(e) => setHost(e.target.value)}
                                                    placeholder={DEFAULTS.host}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{t('emailSendingSettings.portLabel')}</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={port}
                                                    onChange={(e) => setPort(e.target.value)}
                                                    placeholder={String(DEFAULTS.port)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('emailSendingSettings.usernameLabel')}</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            placeholder="info@tabliya.nl"
                                        />
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('emailSendingSettings.appPasswordLabel')}</Form.Label>
                                        {!replacingPassword ? (
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-muted">
                                                    {appPasswordSet
                                                        ? t('emailSendingSettings.appPasswordSaved')
                                                        : t('emailSendingSettings.appPasswordNotSet')}
                                                </span>
                                                <Button variant="outline-secondary" size="sm" onClick={() => setReplacingPassword(true)}>
                                                    {appPasswordSet ? t('emailSendingSettings.replaceButton') : t('emailSendingSettings.setButton')}
                                                </Button>
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control
                                                    type="password"
                                                    value={newAppPassword}
                                                    onChange={(e) => setNewAppPassword(e.target.value)}
                                                    placeholder={t('emailSendingSettings.appPasswordPlaceholder')}
                                                    autoComplete="new-password"
                                                    style={{ maxWidth: 320 }}
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => { setReplacingPassword(false); setNewAppPassword(''); }}
                                                >
                                                    {t('common.cancel')}
                                                </Button>
                                            </div>
                                        )}
                                    </Form.Group>
                                    <p className="text-muted small mb-4">
                                        {t('emailSendingSettings.appPasswordHint')}
                                    </p>

                                    <Form.Check
                                        type="switch"
                                        id="auto-send-shopify-sync"
                                        className="mb-2"
                                        label={t('emailSendingSettings.autoSendToggleLabel')}
                                        checked={autoSendOnShopifySync}
                                        onChange={(e) => setAutoSendOnShopifySync(e.target.checked)}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="auto-send-dry-run"
                                        className="mb-2"
                                        label={t('emailSendingSettings.autoSendDryRunLabel')}
                                        checked={autoSendDryRun}
                                        onChange={(e) => setAutoSendDryRun(e.target.checked)}
                                    />
                                    <Form.Check
                                        type="switch"
                                        id="send-order-confirmation"
                                        className="mb-1"
                                        label={t('emailSendingSettings.sendOrderConfirmationLabel')}
                                        checked={sendOrderConfirmation}
                                        onChange={(e) => setSendOrderConfirmation(e.target.checked)}
                                    />
                                    <p className="text-muted small mb-3" data-testid="order-confirmation-note">
                                        {t('emailSendingSettings.sendOrderConfirmationNote')}{' '}
                                        {sendOrderConfirmation && confirmationGoLiveAt
                                            ? t('emailSendingSettings.sendOrderConfirmationSince', { date: new Date(confirmationGoLiveAt + 'Z').toLocaleString() })
                                            : ''}
                                    </p>
                                    <div className="d-flex align-items-center gap-3 flex-wrap">
                                        <Form.Check
                                            type="switch"
                                            id="send-review-request"
                                            className="mb-1"
                                            label={t('emailSendingSettings.sendReviewRequestLabel')}
                                            checked={sendReviewRequest}
                                            onChange={(e) => setSendReviewRequest(e.target.checked)}
                                        />
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleReviewTest}
                                            disabled={reviewTesting}
                                            data-testid="review-request-send-test"
                                        >
                                            {reviewTesting ? t('emailSendingSettings.sendingTestEmail') : t('emailSendingSettings.sendReviewTestButton')}
                                        </Button>
                                    </div>
                                    <p className="text-muted small mb-3" data-testid="review-request-note">
                                        {t('emailSendingSettings.sendReviewRequestNote', { hours: parseInt(reviewDelayHours, 10) || 72 })}{' '}
                                        {sendReviewRequest && reviewGoLiveAt
                                            ? t('emailSendingSettings.sendReviewRequestSince', { date: new Date(reviewGoLiveAt + 'Z').toLocaleString() })
                                            : ''}
                                    </p>
                                    <Form.Group className="mb-3" controlId="review-request-delay-hours">
                                        <Form.Label>{t('emailSendingSettings.reviewDelayLabel')}</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={1}
                                            max={720}
                                            step={1}
                                            value={reviewDelayHours}
                                            style={{ maxWidth: '8rem' }}
                                            onChange={(e) => setReviewDelayHours(e.target.value)}
                                        />
                                        <Form.Text className="text-muted">{t('emailSendingSettings.reviewDelayHint')}</Form.Text>
                                    </Form.Group>
                                    <Form.Group className="mb-2" controlId="auto-send-test-recipients">
                                        <Form.Label>{t('emailSendingSettings.testRecipientsLabel')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={testRecipients}
                                            placeholder="name@example.com"
                                            className="test-recipients-input"
                                            onChange={(e) => setTestRecipients(e.target.value)}
                                        />
                                        <Form.Text className="text-muted">{t('emailSendingSettings.testRecipientsHint')}</Form.Text>
                                        <div className={`small mt-1 fw-semibold ${savedTestRecipients.trim() ? 'text-success' : 'text-warning'}`} data-testid="test-recipients-status">
                                            {savedTestRecipients.trim()
                                                ? t('emailSendingSettings.testRecipientsStatusRestricted', { list: savedTestRecipients })
                                                : t('emailSendingSettings.testRecipientsStatusEmpty')}
                                        </div>
                                    </Form.Group>
                                    <p className="text-muted small mb-4">
                                        {t('emailSendingSettings.autoSendRulesHint')}{' '}
                                        {goLiveAt
                                            ? t('emailSendingSettings.autoSendGoLive', { date: new Date(goLiveAt + 'Z').toLocaleString() })
                                            : t('emailSendingSettings.autoSendNoGoLive')}
                                    </p>

                                    <Button variant="primary" onClick={handleSave} disabled={saving} className="me-3">
                                        {saving ? t('common.saving') : t('common.save')}
                                    </Button>

                                    <hr className="my-4" />

                                    <h6 className="mb-3">{t('emailSendingSettings.testEmailHeading')}</h6>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <Form.Control
                                            type="email"
                                            value={testEmailTo}
                                            onChange={(e) => setTestEmailTo(e.target.value)}
                                            placeholder={t('emailSendingSettings.testEmailPlaceholder')}
                                            style={{ maxWidth: 320 }}
                                        />
                                        <Button variant="outline-primary" onClick={handleTestEmail} disabled={testing}>
                                            {testing ? t('emailSendingSettings.sendingTestEmail') : t('emailSendingSettings.sendTestEmailButton')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
}
